import { useEffect, useState } from "react";
import { createAdminCity, deleteAdminCity, fetchAdminCities, patchAdminCity } from "../../../admin/api";
import { useCities } from "../../../providers/CitiesContext";
import ActionIconButton from "../shared/ActionIconButton";
import "./CitiesSection.css";

const emptyForm = { name: "", key: "", lat: "", lon: "", zoom: 10, isActive: true, sortOrder: 100 };

function toForm(city) {
  return { name: city.name, key: city.key, lat: city.center?.[0] ?? "", lon: city.center?.[1] ?? "", zoom: city.zoom, isActive: city.isActive, sortOrder: city.sortOrder };
}

function toPayload(form, editing) {
  const payload = { name: form.name.trim(), center: [Number(form.lat), Number(form.lon)], zoom: Number(form.zoom), isActive: form.isActive, sortOrder: Number(form.sortOrder) };
  if (!editing) payload.key = form.key.trim().toLowerCase();
  return payload;
}

export default function CitiesSection() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { refetchCities } = useCities();

  const load = async () => {
    try { const data = await fetchAdminCities(); setItems(Array.isArray(data?.items) ? data.items : []); setError(""); }
    catch (e) { setError(e.message || "Не удалось загрузить города"); }
  };
  useEffect(() => { load(); }, []);

  const change = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const reset = () => { setEditing(null); setForm(emptyForm); setError(""); };
  const save = async (event) => {
    event.preventDefault(); setBusy(true); setError("");
    try {
      if (editing) await patchAdminCity(editing, toPayload(form, true));
      else await createAdminCity(toPayload(form, false));
      reset(); await Promise.all([load(), refetchCities()]);
    } catch (e) { setError(e.message || "Не удалось сохранить город"); }
    finally { setBusy(false); }
  };
  const remove = async (city) => {
    if (!window.confirm(`Удалить город «${city.name}»?`)) return;
    setBusy(true); setError("");
    try { await deleteAdminCity(city._id); await Promise.all([load(), refetchCities()]); }
    catch (e) { setError(e.message || "Не удалось удалить город. Если есть связанные ЖК, отключите его."); }
    finally { setBusy(false); }
  };

  return <section className="admin-cities">
    {error && <div className="admin-cities__error" role="alert">{error}</div>}

    <form className="admin-cities__form-card" onSubmit={save}>
      <div className="admin-cities__form-heading">
        <div>
          <h3>{editing ? "Редактирование города" : "Новый город"}</h3>
          <p>{editing ? "Измените доступные параметры города" : "Заполните параметры для добавления города"}</p>
        </div>
      </div>
      <div className="admin-cities__form-grid">
        <label className="admin-cities__field"><span>Название города</span><input required disabled={busy} value={form.name} onChange={(e) => change("name", e.target.value)} placeholder="Например, Москва" /></label>
        <label className="admin-cities__field"><span>Ключ</span><input required disabled={Boolean(editing) || busy} value={form.key} onChange={(e) => change("key", e.target.value)} pattern="[a-z0-9_-]+" placeholder="moscow" /></label>
        <label className="admin-cities__field"><span>Широта</span><input required disabled={busy} type="number" step="any" min="-90" max="90" value={form.lat} onChange={(e) => change("lat", e.target.value)} placeholder="55.75" /></label>
        <label className="admin-cities__field"><span>Долгота</span><input required disabled={busy} type="number" step="any" min="-180" max="180" value={form.lon} onChange={(e) => change("lon", e.target.value)} placeholder="37.61" /></label>
        <label className="admin-cities__field"><span>Масштаб</span><input required disabled={busy} type="number" min="1" max="20" value={form.zoom} onChange={(e) => change("zoom", e.target.value)} /></label>
        <label className="admin-cities__field"><span>Порядок</span><input required disabled={busy} type="number" value={form.sortOrder} onChange={(e) => change("sortOrder", e.target.value)} /></label>
        <label className="admin-cities__activity">
          <input type="checkbox" disabled={busy} checked={form.isActive} onChange={(e) => change("isActive", e.target.checked)} />
          <span><strong>Город активен</strong><small>Показывать город в публичном селекторе</small></span>
        </label>
      </div>
      <footer className="admin-cities__form-actions">
        {editing && <button type="button" className="admin-cities__secondary-button" disabled={busy} onClick={reset}>Отмена</button>}
        <button type="submit" className="admin-cities__primary-button" disabled={busy}>{busy ? "Сохранение…" : editing ? "Сохранить" : "Добавить город"}</button>
      </footer>
    </form>

    <section className="admin-cities__list-card">
      <header className="admin-cities__list-header">
        <h3>Список городов</h3>
        <span>Всего: {items.length}</span>
      </header>
      {items.length === 0 ? <div className="admin-cities__empty">
        <strong>Города пока не добавлены</strong>
        <p>Добавьте первый город, чтобы он стал доступен для настройки.</p>
        <button type="button" className="admin-cities__primary-button" disabled={busy} onClick={reset}>Добавить город</button>
      </div> : <div className="admin-cities__table-wrap"><table className="admin-cities__table"><colgroup><col className="admin-cities__col-name" /><col className="admin-cities__col-key" /><col className="admin-cities__col-coordinates" /><col className="admin-cities__col-zoom" /><col className="admin-cities__col-status" /><col className="admin-cities__col-order" /><col className="admin-cities__col-actions" /></colgroup><thead><tr><th>Город</th><th>Ключ</th><th>Координаты</th><th>Масштаб</th><th>Статус</th><th>Порядок</th><th>Действия</th></tr></thead><tbody>
        {items.map((city) => <tr key={city._id}><td><strong>{city.name}</strong></td><td><code className="admin-cities__key">{city.key}</code></td><td><span className="admin-cities__coordinates">{city.center?.join(", ")}</span></td><td>{city.zoom}</td><td><span className={`admin-cities__status admin-cities__status--${city.isActive ? "active" : "inactive"}`}>{city.isActive ? "Активен" : "Выключен"}</span></td><td>{city.sortOrder}</td><td><div className="admin-cities__actions"><ActionIconButton icon="edit" label={`Редактировать город ${city.name}`} disabled={busy} onClick={() => { setEditing(city._id); setForm(toForm(city)); }} /><ActionIconButton icon="trash" tone="danger" label={`Удалить город ${city.name}`} disabled={busy} onClick={() => remove(city)} /></div></td></tr>)}
      </tbody></table></div>}
    </section>
  </section>;
}
